import React, { useMemo, useCallback } from "react";
import type { Column, SortColumn, CellClickArgs, Renderers } from "react-data-grid";
import { Row } from "react-data-grid";
import { twMerge } from "tailwind-merge";

import { useDataGridState } from "./useDataGridState";
import type { DataGridConfig } from "./useDataGridConfig";

import { HeaderCell } from "../components/HeaderCell";
import { Cell } from "../components/Cell";
import { NoData } from "../components/NoData";

import { contentAlign } from "../constants";
import { GroupIconCell } from "../components/GroupIconCell";

type RowDecorator<R> = { type: "ROW"; data: R; id: string } | { type: "GROUP_ROW"; data: R[]; id: string };

export function useDataGridAdapter<R extends object>(config: DataGridConfig<R>) {
  // State
  const state = useDataGridState<R>(config);

  // Computed values
  const columns = useMemo(() => {
    const groupIconCol: Column<RowDecorator<R>> = {
      name: "",
      key: "GROUP_ICON_COL",
      width: 40,
      frozen: state.columns.some(c => c.fixed),
      resizable: false,
      sortable: false,
      draggable: false,
      renderHeaderCell: () => <HeaderCell title="" align="center" />,
      renderCell: ({ row }) => (row.type === "GROUP_ROW" ? <GroupIconCell id={row.id} disabled={row.data.length < 2} /> : null),
    };

    const formattedColumns = state.columns.map<Column<RowDecorator<R>>>(col => ({
      name: col.field,
      key: col.field,
      cellClass: twMerge("group/cell", contentAlign[col.align], col.cellClassName),
      headerCellClass: twMerge("group/head-cell", contentAlign[col.align], col.headerCellClassName),
      width: col.width,
      minWidth: col.minWidth,
      maxWidth: col.maxWidth,
      frozen: col.fixed,
      resizable: col.resizable,
      sortable: col.sortable,
      draggable: col.draggable,
      colSpan: () => col.colSpan,
      renderHeaderCell: ({ sortDirection }) => <HeaderCell title={col.headerName} direction={sortDirection} align={col.align} sortable={col.sortable} />,
      renderCell: ({ row }) => <Cell content={row.type === "GROUP_ROW" ? col.renderGroupCell(row.data) : col.renderCell(row.data)} />,
    }));

    return config.groupColumns.length > 0 && formattedColumns.length > 0 ? [groupIconCol, ...formattedColumns] : formattedColumns;
  }, [state.columns]);

  const rows = useMemo(() => {
    return state.rows.reduce<RowDecorator<R>[]>((rows, rowOrRowGroup) => {
      if (Array.isArray(rowOrRowGroup)) {
        const groupRow = {
          type: "GROUP_ROW",
          data: rowOrRowGroup,
          id: config.groupKeyGetter?.(rowOrRowGroup, config.groupColumns) ?? `group-${config.rowKeyGetter(rowOrRowGroup[0])}`,
        } as const;

        rows.push(groupRow);

        return state.expandedRows[groupRow.id] ? rows.concat(rowOrRowGroup.map(r => ({ type: "ROW", data: r, id: config.rowKeyGetter(r) }))) : rows;
      }

      const row = rowOrRowGroup;

      rows.push({
        type: "ROW",
        data: row,
        id: config.rowKeyGetter(row),
      });

      return rows;
    }, []);
  }, [state.rows, state.expandedRows, config.groupKeyGetter, config.rowKeyGetter]);

  const sortColumns = useMemo(() => state.sortFields.map(({ field, direction }) => ({ columnKey: field, direction }) as SortColumn), [state.sortFields]);

  const noRowsFallback = useMemo(() => {
    return (
      config.noData ?? (
        <NoData
          className="col-span-full"
          tableName={config.name}
          loading={config.loading}
          filtered={config.filtered}
          description={config.filteredMessage}
          message={config.emptyMessage}
        />
      )
    );
  }, [config.name, config.loading, config.filtered, config.filteredMessage, config.emptyMessage, config.noData]);

  // Handlers
  const rowKeyGetter = useCallback((row: RowDecorator<R>) => row.id, []);

  const rowHeight = useCallback(
    (row: RowDecorator<R>) => {
      if (typeof config.rowHeight === "number") {
        return config.rowHeight;
      }

      return config.rowHeight(row.data);
    },
    [config.rowHeight]
  );

  const onSortColumnsChange = useCallback(
    (sortColumns: SortColumn[]) => {
      state.changeSortFields(sortColumns.map(({ columnKey, direction }) => ({ field: columnKey, direction })));
    },
    [state.changeSortFields]
  );

  const onCellClick = useCallback(
    ({ row, column }: CellClickArgs<RowDecorator<R>>) => {
      if (column.key === "GROUP_ICON_COL" && row.type === "GROUP_ROW") {
        row.data.length > 1 && state.toggleRowGroup(row.id);
        return;
      }

      config.onRowClick?.(row.data);
      config.onCellClick?.(row.data, config.columnMap[column.key]);
    },
    [config.onRowClick, config.onCellClick, config.columnMap, state.toggleRowGroup]
  );

  const rowRenderer: Renderers<RowDecorator<R>, unknown>["renderRow"] = (key, props) => {
    return config.renderRow ? config.renderRow(key, props.row.data, <Row {...props} />) : <Row key={key} {...props} />;
  };

  const rowClass = (row: RowDecorator<R>, index: number): string | undefined => {
    return config.rowClass?.(row.data, index);
  };

  return {
    rows: columns.length > 0 ? rows : [],
    columns,
    sortColumns,
    noRowsFallback,
    expandedRows: state.expandedRows,
    rowClass,
    rowRenderer,
    rowHeight,
    rowKeyGetter,
    onColumnsReorder: state.changeColumnOrder,
    onSortColumnsChange: onSortColumnsChange,
    onCellClick,
  };
}
